import React, { useCallback, useState, useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Flex, Box, Text, Pressable } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import CrossIcon from '@atlaskit/icon/core/cross';
import { IconButton } from '@atlaskit/button/new';

import {
	ONBOARDING_EXPERIENCES,
	EDITOR_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';

import { extractContentIdFromUrl } from './NthUserOnboardingFlag';
import { PensIcon } from './PensIcon';

// Styles for the rounded rectangle box
const roundedBoxStyles = cssMap({
	root: {
		borderRadius: token('border.radius.200'),
		borderColor: token('color.border.information'),
		boxShadow: token('elevation.shadow.raised'),
		width: '100%',
		maxWidth: '770px',
		padding: token('space.100'),
	},
});

const observerStyles = cssMap({
	root: {
		height: '1px',
		width: '100%',
		visibility: 'hidden',
		pointerEvents: 'none',
	},
});

const outerContainerStyles = cssMap({
	root: {
		display: 'flex',
		justifyContent: 'center',
		zIndex: 800,
	},
});

const innerContainerStyles = cssMap({
	locked: {
		position: 'relative',
		pointerEvents: 'auto',
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		marginBottom: token('space.200'),
		marginTop: token('space.200'),
	},
	floating: {
		position: 'fixed',
		bottom: token('space.200'),
		zIndex: 800,
		pointerEvents: 'auto',
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		marginBottom: token('space.200'),
	},
});

const messages = defineMessages({
	nthUserOnboardingBannerTitle: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.banner.title',
		defaultMessage: 'Next up: start creating',
		description: 'Title of the Nth User Onboarding Banner',
	},
	nthUserOnboardingBannerAction: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.banner.action',
		defaultMessage: 'Try a meeting notes template',
		description: 'Action of the Nth User Onboarding Banner',
	},
	nthUserOnboardingBannerDismiss: {
		id: 'experiment-nth-user-getting-started-page.nth-user-onboarding.banner.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Dismiss button of the Nth User Onboarding Banner',
	},
});

// Styles for the pressable link to remove background
const linkPressableStyles = cssMap({
	root: {
		backgroundColor: token('color.background.information'),
		border: 'none',
		padding: token('space.0'),
		cursor: 'pointer',
	},
});

export const NthUserOnboardingBanner = ({ contentID }: { contentID: string }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [isLocked, setIsLocked] = useState(false);
	const observerRef = useRef<HTMLDivElement>(null);
	const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
	const isLockedRef = useRef(false);
	const hasBeenTriggered = useRef(false);

	const { setOnboardingState } = useSetOnboardingState();
	const {
		data: onboardingStateData,
		loading: isOnboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([
		ONBOARDING_EXPERIENCES.NTH_USER_BANNER_DISMISSED,
		ONBOARDING_EXPERIENCES.IMPROVED_GET_STARTED_NTH_USER_PATH,
		EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
	]);
	const { hasUserPublished, nthUserGSPagePath, nthUserBannerDismissed } =
		deserializeState(onboardingStateData);

	const isCorrectContentId =
		nthUserGSPagePath && extractContentIdFromUrl(nthUserGSPagePath) === contentID;

	// Skip all side effects if user is not eligible
	const isUserEligible =
		!isOnboardingStateLoading &&
		!onboardingStateError &&
		!nthUserBannerDismissed &&
		!hasUserPublished &&
		nthUserGSPagePath &&
		isCorrectContentId;

	useEffect(() => {
		if (!isUserEligible) return;
		isLockedRef.current = isLocked;
		const DELAY_TIMEOUT = 8000;

		const delayTimeout = setTimeout(() => {
			setIsVisible(true);
		}, DELAY_TIMEOUT);

		return () => clearTimeout(delayTimeout);
	}, [isUserEligible, isLocked]);

	useEffect(() => {
		if (!isVisible || !isUserEligible) return;

		const targetElement = observerRef.current;
		if (!targetElement) return;

		let pollInterval: number | null = null;

		const checkPosition = () => {
			const targetRect = targetElement.getBoundingClientRect();
			const isTargetWellBelowViewport = targetRect.top > window.innerHeight + 100;
			if (isTargetWellBelowViewport && isLockedRef.current) {
				setIsLocked(false);
				hasBeenTriggered.current = false;
				if (pollInterval) {
					clearInterval(pollInterval);
					pollInterval = null;
				}
			}
		};

		const handleIntersection = (entries: IntersectionObserverEntry[]) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting && !hasBeenTriggered.current) {
					setIsLocked(true);
					hasBeenTriggered.current = true;
					pollInterval = setInterval(checkPosition, 100);
				}
			});
		};

		intersectionObserverRef.current = new IntersectionObserver(handleIntersection, {
			root: null,
			rootMargin: '0px',
			threshold: 0,
		});
		intersectionObserverRef.current.observe(targetElement);

		return () => {
			if (intersectionObserverRef.current) {
				intersectionObserverRef.current.disconnect();
			}
			if (pollInterval) {
				clearInterval(pollInterval);
			}
		};
	}, [isVisible, isUserEligible]);

	const handleDismiss = useCallback(async () => {
		setIsVisible(false);
		setOnboardingState({
			key: ONBOARDING_EXPERIENCES.NTH_USER_BANNER_DISMISSED,
			value: 'true',
		});
	}, [setOnboardingState]);

	const handleTemplateClick = useCallback(async () => {
		// TODO: Implement template click logic
	}, []);
	if (!isVisible || !isUserEligible) {
		return null;
	}

	return (
		<>
			<Box ref={observerRef} xcss={observerStyles['root']} />
			<Box xcss={outerContainerStyles['root']}>
				<Box xcss={innerContainerStyles[isLocked ? 'locked' : 'floating']}>
					<Box xcss={roundedBoxStyles['root']} backgroundColor="color.background.information">
						<Flex justifyContent="space-between" alignItems="center">
							<Flex alignItems="center" gap="space.200">
								<Box>
									<PensIcon size={50} />
								</Box>
								<Text weight="bold">
									<FormattedMessage {...messages.nthUserOnboardingBannerTitle} />
								</Text>
							</Flex>
							<Flex alignItems="center" gap="space.100">
								<Pressable onClick={handleTemplateClick} xcss={linkPressableStyles['root']}>
									<Text color="color.link">
										<FormattedMessage {...messages.nthUserOnboardingBannerAction} />
									</Text>
								</Pressable>
								<IconButton
									onClick={handleDismiss}
									appearance="subtle"
									icon={(iconProps) => <CrossIcon {...iconProps} />}
									label={messages.nthUserOnboardingBannerDismiss.defaultMessage}
								/>
							</Flex>
						</Flex>
					</Box>
				</Box>
			</Box>
		</>
	);
};
